import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";

export function QuickAccessControlsCard() {
  const [maint, setMaint] = useState(null);
  const [signups, setSignups] = useState(null);
  const [signin, setSignin] = useState(null);
  const [busy, setBusy] = useState("");

  const reload = async () => {
    try {
      const [m, su, si] = await Promise.all([
        api.get("/admin/maintenance-mode"),
        api.get("/admin/signups-gate"),
        api.get("/admin/signin-gate"),
      ]);
      setMaint(m.data);
      setSignups(su.data);
      setSignin(si.data);
    } catch (e) {
      // transient — toggles stay disabled until the next render
    }
  };

  useEffect(() => { reload(); }, []);

  const flipMaint = async (next) => {
    setBusy("maint");
    try {
      const r = await api.put("/admin/maintenance-mode", {
        enabled: next,
        banner: maint?.banner || "",
      });
      setMaint(r.data);
      const [su, si] = await Promise.all([
        api.get("/admin/signups-gate"),
        api.get("/admin/signin-gate"),
      ]);
      setSignups(su.data);
      setSignin(si.data);
      toast.success(next
        ? "Maintenance mode ON — sign-ups and sign-in paused"
        : "Maintenance mode OFF — platform reopened");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy("");
    }
  };

  const flipGate = async (kind, disabled) => {
    setBusy(kind);
    const endpoint = kind === "signups" ? "/admin/signups-gate" : "/admin/signin-gate";
    const setter = kind === "signups" ? setSignups : setSignin;
    const current = kind === "signups" ? signups : signin;
    try {
      const r = await api.put(endpoint, {
        disabled,
        message: current?.message || "",
      });
      setter({ ...current, ...r.data });
      toast.success(
        kind === "signups"
          ? (disabled ? "Sign-ups paused" : "Sign-ups re-opened")
          : (disabled ? "Customer sign-in paused" : "Customer sign-in re-opened")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy("");
    }
  };

  const ready = maint && signups && signin;

  return (
    <div
      className="bg-gradient-to-br from-[#184A30] to-[#0F3221] text-[#FBF9F6] rounded-2xl p-8 shadow-lg"
      data-testid="quick-access-controls-card"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#CBA35C]">Quick access</p>
          <h2 className="serif text-3xl mt-1">Customer access controls</h2>
          <p className="text-sm text-[#FBF9F6]/70 mt-1 max-w-xl">
            Instantly turn customer sign-up and sign-in on or off. Admins and managers can always sign in, so you can never lock yourself out.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            const trigger = document.querySelector('[data-testid="tab-controls"]');
            if (trigger) trigger.click();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="text-xs tracking-widest uppercase underline underline-offset-4 hover:text-[#CBA35C] transition"
          data-testid="quick-access-jump-controls"
        >
          More settings →
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <QuickToggle
          label="Maintenance mode"
          hint={maint?.enabled ? "Platform frozen for customers" : "Normal operation"}
          checked={!!maint?.enabled}
          onChange={(v) => flipMaint(v)}
          disabled={!ready || busy === "maint"}
          accent={maint?.enabled}
          testid="quick-toggle-maintenance"
        />
        <QuickToggle
          label="Customer sign-ups"
          hint={signups?.disabled ? "New registrations paused" : "Open — anyone can register"}
          checked={!signups?.disabled}
          onChange={(v) => flipGate("signups", !v)}
          disabled={!ready || busy === "signups"}
          accent={signups?.disabled}
          testid="quick-toggle-signups"
        />
        <QuickToggle
          label="Customer sign-in"
          hint={signin?.disabled ? "Customer logins paused (admins still allowed)" : "Open — customers can log in"}
          checked={!signin?.disabled}
          onChange={(v) => flipGate("signin", !v)}
          disabled={!ready || busy === "signin"}
          accent={signin?.disabled}
          testid="quick-toggle-signin"
        />
      </div>
    </div>
  );
}

function QuickToggle({ label, hint, checked, onChange, disabled, accent, testid }) {
  return (
    <div
      className={`rounded-xl border p-4 flex items-start justify-between gap-3 transition ${
        accent
          ? "bg-[#CBA35C]/15 border-[#CBA35C]/40"
          : "bg-white/5 border-white/10"
      }`}
      data-testid={testid}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] tracking-widest uppercase text-[#FBF9F6]/60">{label}</p>
        <p className="text-sm font-medium mt-1 text-[#FBF9F6]">
          {accent ? "OFF" : "ON"}
        </p>
        <p className="text-[11px] text-[#FBF9F6]/65 mt-1">{hint}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        data-testid={`${testid}-switch`}
      />
    </div>
  );
}
